import { Box, Chip, Divider, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  AccountCircle,
  AdminPanelSettingsOutlined,
  Group,
  HubOutlined,
  Lock,
  Palette,
  Security,
} from '@mui/icons-material';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useEkko from 'src/Common/Providers/EkkoProvider';
import Permissions from 'src/Common/Permissions';
import {
  NAV_BG_ACTIVE,
  NAV_BG_HOVER,
  NAV_COLOR_ACTIVE,
  NAV_COLOR_INACTIVE,
  NAV_ICON_GAP,
  NAV_LABEL_FONT_SIZE,
  NAV_LABEL_FONT_WEIGHT,
  NAV_PILL_INSET,
  NAV_PILL_RADIUS,
  NAV_STRIPE_COLOR,
  NAV_STRIPE_WIDTH,
  NAV_TRANSITION,
  navPillSx,
} from 'src/Common/Components/Navigation/navItemStyle';

interface SettingsSection {
  value: string;
  label: string;
  icon: React.ReactElement;
  /** When set, the path segment after /me/. Defaults to `value`. */
  segment?: string;
  /** Optional permission gate. Hidden when the user lacks it. */
  requires?: Permissions;
  /** Render a small "Beta" chip next to the label. */
  beta?: boolean;
}

/**
 * Wraps a section's label so a small "Beta" chip can sit next to it without
 * widening the API of every Tab consumer.
 */
const renderLabel = (section: SettingsSection): React.ReactNode => {
  if (!section.beta) {
    return section.label;
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1} component="span">
      <span>{section.label}</span>
      <Chip
        label="Beta"
        size="small"
        color="primary"
        variant="outlined"
        sx={{ height: 18, fontSize: 9, fontWeight: 700, letterSpacing: 0.3, '& .MuiChip-label': { px: 0.75 } }}
      />
    </Stack>
  );
};

const PERSONAL_SECTIONS: SettingsSection[] = [
  { value: 'profile', label: 'Profile', icon: <AccountCircle /> },
  { value: 'security', label: 'Security', icon: <Lock /> },
  { value: 'appearance', label: 'Appearance', icon: <Palette /> },
  { value: 'roles', label: 'Roles & Permissions', icon: <Security /> },
];

const ADMIN_SECTIONS: SettingsSection[] = [
  {
    value: 'admin-users',
    label: 'Users',
    icon: <Group />,
    segment: 'admin/users',
    requires: Permissions.USERS_READ,
  },
  {
    value: 'admin-roles',
    label: 'Roles',
    icon: <AdminPanelSettingsOutlined />,
    segment: 'admin/roles',
    requires: Permissions.ROLES_READ,
  },
  {
    value: 'admin-integrations',
    label: 'Integrations',
    icon: <HubOutlined />,
    segment: 'admin/integrations',
    beta: true,
  },
];

// MUI Tabs uses `false` to mean "no tab is selected here" so the indicator
// stays off the inactive Tabs instance.
type ActiveValue = string | false;

// Shared nav-item look (see navItemStyle): muted inactive text, matching label
// typography, and a rounded inset pill with the shared transition. Mobile uses
// this as-is; the desktop variant overrides the geometry to anchor at the right.
const tabRootSx = {
  justifyContent: { md: 'flex-start' },
  minHeight: 48,
  textTransform: 'none',
  color: NAV_COLOR_INACTIVE,
  fontSize: NAV_LABEL_FONT_SIZE,
  fontWeight: NAV_LABEL_FONT_WEIGHT,
  // Explicit per-side margins (not `mx`) so the desktop variant's navPillSx can
  // override one side cleanly, without a leftover shorthand key.
  marginLeft: NAV_PILL_INSET,
  marginRight: NAV_PILL_INSET,
  borderRadius: NAV_PILL_RADIUS,
  transition: NAV_TRANSITION,
  '&:hover': { backgroundColor: NAV_BG_HOVER },
  // Selected reads as a subtle filled pill with neutral text; the accent is
  // carried by the stripe indicator, not by recoloring the label.
  '&.Mui-selected': { color: NAV_COLOR_ACTIVE, backgroundColor: NAV_BG_ACTIVE },
};

const tabSx = {
  '& .MuiTab-root': tabRootSx,
  '& .MuiTab-iconWrapper': { marginRight: NAV_ICON_GAP },
};

// Desktop (vertical) tabs mirror the main nav rather than copy it: the active
// indicator is a full-height primary stripe flush against the rail's RIGHT
// border — pointing toward the settings content. The pill shape (flush + squared
// on the right) comes from the shared navPillSx and lives in the base, so hover
// and selection render the same shape. (Mobile keeps fully-rounded pills + the
// default bottom bar.)
const verticalTabSx = {
  ...tabSx,
  '& .MuiTab-root': { ...tabRootSx, ...navPillSx('right') },
  '& .MuiTabs-indicator': {
    // Flush against the rail's right border.
    right: 0,
    left: 'auto',
    width: NAV_STRIPE_WIDTH,
    backgroundColor: NAV_STRIPE_COLOR,
  },
};

export const SettingsNav = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { hasPermission } = useEkko();

  const visibleAdmin = useMemo(
    () => ADMIN_SECTIONS.filter((s) => s.requires === undefined || hasPermission(s.requires)),
    [hasPermission],
  );

  const allSections = useMemo(() => [...PERSONAL_SECTIONS, ...visibleAdmin], [visibleAdmin]);

  // Derive the active value from the path. We match by prefix instead of
  // exact equality so admin sub-routes (e.g. /me/admin/users/:id/mappings)
  // keep the parent tab (Users) highlighted. Longest-segment wins so
  // `admin/users` beats nothing when on `admin/users/123/mappings`.
  const active = useMemo(() => {
    const trimmed = pathname.replace(/^\/me\/?/, '');
    const match = [...allSections]
      .sort((a, b) => (b.segment ?? b.value).length - (a.segment ?? a.value).length)
      .find((s) => {
        const segment = s.segment ?? s.value;
        return trimmed === segment || trimmed.startsWith(segment + '/');
      });
    return match?.value ?? allSections[0].value;
  }, [pathname, allSections]);

  const handleChange = (value: string): void => {
    const section = allSections.find((s) => s.value === value);
    if (section) {
      navigate('/me/' + (section.segment ?? section.value));
    }
  };

  if (!isDesktop) {
    // Mobile: single flat scrollable strip. Group label/divider don't
    // lay out cleanly in horizontal mode, so collapse the grouping.
    return (
      <Tabs
        orientation={'horizontal'}
        value={active}
        onChange={(_, value) => handleChange(value)}
        variant={'scrollable'}
        scrollButtons={'auto'}
        allowScrollButtonsMobile
        sx={tabSx}
      >
        {allSections.map((section) => (
          <Tab
            key={section.value}
            value={section.value}
            label={renderLabel(section)}
            icon={section.icon}
            iconPosition={'start'}
          />
        ))}
      </Tabs>
    );
  }

  const isPersonalActive = PERSONAL_SECTIONS.some((s) => s.value === active);
  const personalValue: ActiveValue = isPersonalActive ? active : false;
  const adminValue: ActiveValue = !isPersonalActive ? active : false;

  return (
    <Box>
      <Tabs
        orientation={'vertical'}
        value={personalValue}
        onChange={(_, value) => handleChange(value)}
        sx={verticalTabSx}
      >
        {PERSONAL_SECTIONS.map((section) => (
          <Tab
            key={section.value}
            value={section.value}
            label={renderLabel(section)}
            icon={section.icon}
            iconPosition={'start'}
          />
        ))}
      </Tabs>

      {visibleAdmin.length > 0 && (
        <>
          <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
            <Divider />
            <Typography
              variant={'caption'}
              sx={{
                display: 'block',
                pt: 1.5,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              Administration
            </Typography>
          </Box>
          <Tabs
            orientation={'vertical'}
            value={adminValue}
            onChange={(_, value) => handleChange(value)}
            sx={verticalTabSx}
          >
            {visibleAdmin.map((section) => (
              <Tab
                key={section.value}
                value={section.value}
                label={renderLabel(section)}
                icon={section.icon}
                iconPosition={'start'}
              />
            ))}
          </Tabs>
        </>
      )}
    </Box>
  );
};
